import { type } from 'arktype';
import { createPaginatedResponseSchema } from '../shared/pagination';

/**
 * Los cinco documentos legales que se pueden aceptar (respuestas del cliente 2026-09-17, P-01:
 * «Nadie guarda las aceptaciones. La app recoge las cuatro casillas y la firma de la matrícula
 * en el navegador [...] Hace falta una tabla: quién, qué versión del texto, cuándo y desde
 * dónde»). Mismo estilo que el resto de enumerados del repositorio: constante `as const`
 * exportada + unión de literales ArkType (`type.enumerated(...)`).
 */
export const LEGAL_DOCUMENTS = ['TERMS_AND_PRIVACY', 'MATRICULA', 'JOIN', 'ORGANIZE', 'COLLABORATOR'] as const;
export const legalDocumentSchema = type.enumerated(...LEGAL_DOCUMENTS);
export type LegalDocument = typeof legalDocumentSchema.infer;

/**
 * `POST /legal-acceptances` (P-01): registra UNA aceptación — la tabla es APPEND-ONLY, nunca se
 * actualiza ni se borra (ver la migración `20260917132813_respuestasCliente.sql`).
 * `experienceId` solo aplica cuando `documento` es `JOIN` u `ORGANIZE` (aceptar unirse a un plan
 * concreto o las condiciones de organizarlo); `signature` solo cuando es `MATRICULA`. Ninguno de
 * los dos se valida aquí como condicionado al otro: el servicio decide qué exigir según
 * `documento`, este contrato solo declara la forma. Deliberadamente SIN `ip` ni `userAgent`: los
 * añade el backend a partir de la propia petición, nunca del cuerpo (mismo criterio que
 * `coordinatorUserId` en `createExperienceSchema` — nunca lo elige quien manda el cuerpo).
 *
 * `texto` y `imagenCedida` entran con la entrega del cliente del 18/09/2026
 * (`2-legal/consentimientos-y-firma.md` §2, migración
 * `20260918120000_aceptacionesLegalesTextoYCesionDeImagen.sql`). `texto` es OBLIGATORIO — es la
 * propia prueba: «no basta la versión, no hay histórico de versiones en ningún sitio, así que la
 * prueba es el texto». `imagenCedida` es opcional (no toda aceptación pregunta por la cesión de
 * imagen) y, cuando llega, es un booleano real, nunca un valor por defecto inventado por el
 * contrato — `false` significa "dijo que no", ausente significa "no se le preguntó".
 *
 * DOS DESVIACIONES CONSCIENTES respecto al modelo `LegalAcceptance` que propone el cliente en
 * `2-legal/consentimientos-y-firma.md` §2 — quien las lea tiene que saber que no son un olvido:
 *
 * 1. **Sigue sin `@@unique([userId, kind, experienceId, version])`.** El cliente propone que "una
 *    persona no acepta dos veces lo mismo: se sustituye". Aquí la tabla nació APPEND-ONLY
 *    (`20260917132813_respuestasCliente.sql`) y un histórico completo de cada vez que alguien
 *    aceptó prueba más que una fila que un `upsert` fue sustituyendo — perder las aceptaciones
 *    anteriores sería perder prueba, no limpiar ruido.
 * 2. **`userId` sigue `NOT NULL`.** El modelo del cliente lo hace nullable porque en su
 *    repositorio el consentimiento del alta se da ANTES de que exista la cuenta (`email` viaja
 *    en su lugar y se reconcilia al confirmar el correo — un alta de seis pasos). En este
 *    sistema el alta CREA la cuenta primero y la casilla de consentimiento va después, así que
 *    siempre hay un `userId` real que registrar: no hace falta `email` ni reconciliación.
 */
export const createLegalAcceptanceSchema = type({
	documento: legalDocumentSchema,
	'experienceId?': 'string.uuid',
	'imagenCedida?': 'boolean',
	'signature?': 'string',
	texto: 'string',
	version: 'string',
}).onUndeclaredKey('delete');

export type CreateLegalAcceptance = typeof createLegalAcceptanceSchema.infer;

/**
 * La respuesta de `POST /legal-acceptances` (P-01): la fila tal cual queda en
 * `"legalAcceptance"`. `ip` es opcional/nullable porque la columna `INET` puede quedar sin
 * resolver (p. ej. en pruebas o detrás de un proxy que no la reenvía). `texto`/`imagenCedida`
 * son opcionales Y nullable (`| null`): las filas anteriores a la migración
 * `20260918120000_aceptacionesLegalesTextoYCesionDeImagen.sql` no los tienen (columnas
 * nullable a propósito, sin backfill inventado) y las nuevas los guardan tal cual, incluido
 * `imagenCedida: false` cuando la persona dijo explícitamente que no.
 */
export const legalAcceptanceResponseSchema = type({
	acceptedAt: 'string.date.iso',
	documento: legalDocumentSchema,
	'experienceId?': 'string.uuid | null',
	id: 'string.uuid',
	'imagenCedida?': 'boolean | null',
	'ip?': 'string | null',
	'signature?': 'string | null',
	'texto?': 'string | null',
	'userAgent?': 'string | null',
	userId: 'string.uuid',
	version: 'string',
}).onUndeclaredKey('delete');

export type LegalAcceptanceResponse = typeof legalAcceptanceResponseSchema.infer;

/**
 * `GET /legal-acceptances` (deuda cerrada de la tarea de cimientos): hasta ahora el endpoint
 * devolvía un array plano porque este contrato, congelado para la tarea que lo introdujo, no
 * declaraba una respuesta paginada — el resto del repositorio pagina por cursor (ver `GET
 * /invitations` como referencia de forma). Misma factoría que ya usa el resto de listados
 * (`createPaginatedResponseSchema`, `contracts/shared/pagination`), sin filtro propio: a
 * diferencia de `invitationPaginatedResponseSchema`, esta lista no admite ningún parámetro
 * adicional además de `cursor`/`limit` (`paginationSchema` sin `.merge()`).
 */
export const legalAcceptancePaginatedResponseSchema = createPaginatedResponseSchema(legalAcceptanceResponseSchema);
export type LegalAcceptancePaginatedResponse = typeof legalAcceptancePaginatedResponseSchema.infer;
